import React, { useEffect, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Plus,
    CaretDown,
    Trash,
    PushPin,
    Books,
    ArrowLeft,
    Users,
    Calendar,
    Layout,
    User,
    ShareNetwork,
    ArrowsClockwise,
    PencilSimple,
} from '@phosphor-icons/react';
import { Button } from '@t-one-internal/design-system';
import {
    dashboardService,
    widgetService,
    widgetTemplateService,
    pageService,
} from '../services/widgetService';
import { WidgetCanvas } from '@t-one-internal/design-system';
import { WidgetWrapper } from '../components/widgets/WidgetWrapper';
import WidgetTemplateLibrary from '../components/widgets/WidgetTemplateLibrary';
import { useDashboardsStore } from '../stores/dashboardsStore';
import { useSharedDashboardsStore } from '../stores/sharedDashboardsStore';
import { ShareDashboardModal } from '../components/dashboard/ShareDashboardModal';
import { DrillToDetailModal } from '../components/widgets/DrillToDetailModal';
import type { Dashboard, DashboardWidget, WidgetTemplate, WidgetLayoutUpdate, SharedDashboard } from '../types/widget';
import type { ChartType, QueryFilter } from '../types/analytics';
import type { DrillToDetailContext } from '../types/drillToDetail';

type TabType = 'my-dashboards' | 'shared-dashboards';

// Page key for My Dashboards
const MY_DASHBOARD_PAGE_KEY = 'my_dashboards';

// ============================================
// Helper Functions
// ============================================

// Normalize configs coming from the API (they may arrive as JSON strings)
const parseConfig = (config: unknown): Record<string, unknown> => {
    if (!config) return {};

    let parsed: Record<string, unknown>;

    if (typeof config === 'string') {
        try {
            parsed = JSON.parse(config);
        } catch (error) {
            console.warn('Failed to parse widget config JSON, falling back to empty object', error);
            return {};
        }
    } else {
        parsed = config as Record<string, unknown>;
    }

    return parsed;
};

// Convert DashboardWidget to canvas widget format
const convertToCanvasWidget = (widget: DashboardWidget) => {
    const visualizationConfig = parseConfig(widget.visualization_config);
    const queryConfig = parseConfig(widget.query_config);

    // Extract layout constraints from capabilities.visualization.layout
    const layoutConstraints = widget.capabilities?.visualization?.layout;

    return {
        id: String(widget.id),
        type: (visualizationConfig?.chart_type as 'stat' | 'chart' | 'table' | 'map' | 'custom') || 'custom',
        title: widget.title,
        layout: {
            i: String(widget.id),
            x: widget.position_x,
            y: widget.position_y,
            w: widget.width,
            h: widget.height,
            minW: layoutConstraints?.min_width ?? 2,
            minH: layoutConstraints?.min_height ?? 2,
            maxW: layoutConstraints?.max_width,
            maxH: layoutConstraints?.max_height,
        },
        data: queryConfig,
        config: visualizationConfig,
    };
};

// ============================================
// Dashboard Dropdown Component
// ============================================

interface DashboardDropdownProps {
    dashboards: Dashboard[];
    currentDashboard: Dashboard | null;
    onSelect: (dashboard: Dashboard) => void;
    onCreateNew: () => void;
    onSetDefault: (id: number) => void;
    onDelete: (id: number) => void;
}

const DashboardDropdown: React.FC<DashboardDropdownProps> = ({
    dashboards,
    currentDashboard,
    onSelect,
    onCreateNew,
    onSetDefault,
    onDelete,
}) => {
    const [isOpen, setIsOpen] = React.useState(false);

    return (
        <div style={{ position: 'relative' }}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    padding: '8px 12px',
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minWidth: '200px',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s ease',
                }}
            >
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                    {currentDashboard?.name || 'Select Dashboard'}
                </span>
                <CaretDown size={16} weight="bold" style={{ color: '#6b7280' }} />
            </button>

            {isOpen && (
                <>
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 99,
                        }}
                        onClick={() => setIsOpen(false)}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            marginTop: '4px',
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            minWidth: '280px',
                            zIndex: 100,
                            overflow: 'hidden',
                        }}
                    >
                        <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                            {dashboards.map((dashboard) => (
                                <div
                                    key={dashboard.id}
                                    style={{
                                        padding: '12px 16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                        backgroundColor:
                                            currentDashboard?.id === dashboard.id ? '#f3f4f6' : 'transparent',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (currentDashboard?.id !== dashboard.id) {
                                            e.currentTarget.style.backgroundColor = '#f9fafb';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (currentDashboard?.id !== dashboard.id) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                    onClick={() => {
                                        onSelect(dashboard);
                                        setIsOpen(false);
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!dashboard.is_default) {
                                                    onSetDefault(dashboard.id);
                                                }
                                            }}
                                            style={{
                                                padding: '2px',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                cursor: dashboard.is_default ? 'default' : 'pointer',
                                                borderRadius: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                            title={dashboard.is_default ? 'Default dashboard' : 'Set as default'}
                                        >
                                            {dashboard.is_default ? (
                                                <PushPin size={14} weight="fill" style={{ color: '#3b82f6' }} />
                                            ) : (
                                                <PushPin size={14} style={{ color: '#d1d5db' }} />
                                            )}
                                        </button>
                                        <span style={{ fontSize: '14px', color: '#111827' }}>
                                            {dashboard.name}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (confirm('Delete this dashboard?')) {
                                                    onDelete(dashboard.id);
                                                }
                                            }}
                                            style={{
                                                padding: '4px',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                borderRadius: '4px',
                                            }}
                                            title="Delete"
                                        >
                                            <Trash size={14} style={{ color: '#ef4444' }} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{ borderTop: '1px solid #e5e7eb', padding: '8px' }}>
                            <Button
                                variant="secondary"
                                className="w-full"
                                onClick={() => {
                                    onCreateNew();
                                    setIsOpen(false);
                                }}
                            >
                                <Plus size={16} weight="bold" />
                                Create New Dashboard
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// ============================================
// Create Dashboard Modal Component
// ============================================

interface CreateDashboardModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (name: string, description: string) => void;
}

const CreateDashboardModal: React.FC<CreateDashboardModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
}) => {
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (name.trim()) {
            onSubmit(name.trim(), description.trim());
            setName('');
            setDescription('');
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '24px',
                    width: '400px',
                    maxWidth: '90vw',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>
                    Create New Dashboard
                </h2>
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '16px' }}>
                        <label
                            style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}
                        >
                            Dashboard Name *
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="My Dashboard"
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                            }}
                            autoFocus
                        />
                    </div>
                    <div style={{ marginBottom: '24px' }}>
                        <label
                            style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}
                        >
                            Description (optional)
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Add a description..."
                            rows={3}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                                resize: 'vertical',
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={!name.trim()}
                        >
                            Create Dashboard
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ============================================
// Edit Dashboard Modal Component
// ============================================

interface EditDashboardModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (name: string, description: string) => void;
    dashboard: Dashboard;
}

const EditDashboardModal: React.FC<EditDashboardModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    dashboard,
}) => {
    const [name, setName] = React.useState(dashboard.name);
    const [description, setDescription] = React.useState(dashboard.description || '');

    React.useEffect(() => {
        if (isOpen) {
            setName(dashboard.name);
            setDescription(dashboard.description || '');
        }
    }, [isOpen, dashboard]);

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (name.trim()) {
            onSubmit(name.trim(), description.trim());
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '24px',
                    width: '400px',
                    maxWidth: '90vw',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>
                    Edit Dashboard
                </h2>
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '16px' }}>
                        <label
                            style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}
                        >
                            Dashboard Name *
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="My Dashboard"
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                            }}
                            autoFocus
                        />
                    </div>
                    <div style={{ marginBottom: '24px' }}>
                        <label
                            style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}
                        >
                            Description (optional)
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Add a description..."
                            rows={3}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                                resize: 'vertical',
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={!name.trim()}
                        >
                            Save Changes
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ============================================
// Main DashboardsPage Component
// ============================================

interface DashboardsPageProps {
  pageKey?: string;
}

const DashboardsPage: React.FC<DashboardsPageProps> = ({ pageKey: pageKeyProp }) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Get active tab from URL or default to 'my-dashboards'
    const activeTab = (searchParams.get('tab') as TabType) || 'my-dashboards';

    // Get state and actions from dashboards store (My Dashboards)
    const {
        currentPage,
        dashboards: myDashboards,
        currentDashboard,
        dashboardsLoading,
        widgets,
        widgetsLoading,
        templates,
        templatesLoading,
        isTemplateLibraryOpen,
        isDashboardCreateModalOpen: isCreateModalOpen,
        error,
        setCurrentPage,
        setDashboards: setMyDashboards,
        setCurrentDashboard,
        addDashboard,
        updateDashboard,
        removeDashboard,
        setDashboardsLoading,
        setWidgets,
        addWidget,
        updateWidget,
        removeWidget,
        updateWidgetLayouts,
        setWidgetsLoading,
        setTemplates,
        setTemplatesLoading,
        setTemplateLibraryOpen: setIsTemplateLibraryOpen,
        setDashboardCreateModalOpen: setIsCreateModalOpen,
        setError,
    } = useDashboardsStore();

    // Get state and actions from shared dashboards store
    const {
        sharedDashboards,
        sharedDashboardsLoading,
        selectedDashboard: selectedSharedDashboard,
        dashboardOwner: sharedDashboardOwner,
        widgets: sharedWidgets,
        widgetsLoading: sharedWidgetsLoading,
        error: sharedError,
        setSharedDashboards,
        setSharedDashboardsLoading,
        setSelectedDashboard: setSelectedSharedDashboard,
        setDashboardOwner: setSharedDashboardOwner,
        setWidgets: setSharedWidgets,
        setWidgetsLoading: setSharedWidgetsLoading,
        setError: setSharedError,
        clearSelection: clearSharedSelection,
    } = useSharedDashboardsStore();

    // Share modal state
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    // Edit dashboard modal state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    // Drill-to-detail state
    const [drillToDetailContext, setDrillToDetailContext] = useState<DrillToDetailContext | null>(null);

    const handleDrillToDetail = useCallback((context: DrillToDetailContext) => {
        setDrillToDetailContext(context);
    }, []);

    // Compute loading state based on active tab
    const loading = activeTab === 'my-dashboards' ? dashboardsLoading : sharedDashboardsLoading;

    // Determine if we're viewing a shared dashboard detail
    const isViewingSharedDashboard = selectedSharedDashboard !== null;

    // Clear store state when component mounts to avoid showing stale data from other pages
    useEffect(() => {
        // Clear widgets and dashboards from previous page (e.g., My Portal)
        setWidgets([]);
        setCurrentDashboard(null);
        setCurrentPage(null);
    }, []); // Empty dependency - only run on mount

    // Handle tab change
    const handleTabChange = (tab: TabType) => {
        setSearchParams({ tab });
        clearSharedSelection();
    };

    // Load page for My Dashboards
    useEffect(() => {
        if (activeTab !== 'my-dashboards') return;

        const loadPage = async () => {
            try {
                const page = await pageService.getPageByKey(pageKeyProp ?? MY_DASHBOARD_PAGE_KEY);
                setCurrentPage(page);
            } catch (err) {
                console.error('Failed to load page:', err);
                setError('Failed to load page');
            }
        };

        loadPage();
    }, [activeTab, setCurrentPage, setError]);

    // Load dashboards for My Dashboards tab
    useEffect(() => {
        if (activeTab !== 'my-dashboards' || !currentPage?.key) return;

        const loadDashboards = async () => {
            setDashboardsLoading(true);
            setError(null);
            try {
                const dashboardList = await dashboardService.getDashboardsByPageKey(currentPage.key);
                setMyDashboards(dashboardList);

                // Set current dashboard to default or first
                if (dashboardList.length > 0) {
                    const defaultDashboard = dashboardList.find((d) => d.is_default) || dashboardList[0];
                    setCurrentDashboard(defaultDashboard);
                } else {
                    setCurrentDashboard(null);
                }
            } catch (err) {
                console.error('Failed to load dashboards:', err);
                setError('Failed to load dashboards');
            } finally {
                setDashboardsLoading(false);
            }
        };

        loadDashboards();
    }, [activeTab, currentPage, setMyDashboards, setCurrentDashboard, setDashboardsLoading, setError]);

    // Load shared dashboards
    useEffect(() => {
        if (activeTab !== 'shared-dashboards') return;

        const loadSharedDashboards = async () => {
            setSharedDashboardsLoading(true);
            setSharedError(null);
            try {
                const dashboardList = await dashboardService.getDashboardsSharedWithMe();
                setSharedDashboards(dashboardList as any);
            } catch (err) {
                console.error('Failed to load shared dashboards:', err);
                setSharedError('Failed to load dashboards');
            } finally {
                setSharedDashboardsLoading(false);
            }
        };

        loadSharedDashboards();
    }, [activeTab, setSharedDashboards, setSharedDashboardsLoading, setSharedError]);

    // Handle selecting a shared dashboard (fetch with owner context)
    const handleSelectSharedDashboard = useCallback(async (dashboard: SharedDashboard | Dashboard) => {
        setSharedWidgetsLoading(true);
        setSharedError(null);
        try {
            const detail = await dashboardService.getSharedDashboardDetail(dashboard.id);
            setSelectedSharedDashboard(detail.dashboard);
            setSharedDashboardOwner(detail.owner);
            setSharedWidgets(detail.widgets);
        } catch (err) {
            console.error('Failed to load shared dashboard:', err);
            setSharedError('Failed to load dashboard');
        } finally {
            setSharedWidgetsLoading(false);
        }
    }, [setSelectedSharedDashboard, setSharedDashboardOwner, setSharedWidgets, setSharedWidgetsLoading, setSharedError]);

    // Load widgets when dashboard changes (only for my dashboards - shared dashboards are loaded with owner context)
    useEffect(() => {
        // Only load widgets for my dashboards - shared dashboard widgets are loaded via handleSelectSharedDashboard
        if (activeTab !== 'my-dashboards') return;

        if (!currentDashboard) {
            setWidgets([]);
            return;
        }

        const loadWidgets = async () => {
            setWidgetsLoading(true);
            try {
                const widgetList = await widgetService.getWidgetsByDashboard(currentDashboard.id);
                setWidgets(widgetList);
            } catch (err) {
                console.error('Failed to load widgets:', err);
                setError('Failed to load widgets');
            } finally {
                setWidgetsLoading(false);
            }
        };

        loadWidgets();
    }, [activeTab, currentDashboard, setWidgets, setWidgetsLoading, setError]);

    // Load templates for My Dashboards
    useEffect(() => {
        if (activeTab !== 'my-dashboards' || !currentPage?.key) return;

        const loadTemplates = async () => {
            setTemplatesLoading(true);
            try {
                const templateList = await widgetTemplateService.getTemplatesForPage(currentPage.key);
                setTemplates(templateList);
            } catch (err) {
                console.error('Failed to load templates:', err);
            } finally {
                setTemplatesLoading(false);
            }
        };

        loadTemplates();
    }, [activeTab, currentPage, setTemplates, setTemplatesLoading]);

    // Dashboard actions
    const handleCreateDashboard = async (name: string, description: string) => {
        if (!currentPage?.key) {
            setError('Page not loaded. Please refresh and try again.');
            return;
        }

        try {
            const newDashboard = await dashboardService.createDashboard(currentPage.key, {
                page_id: currentPage.id,
                name,
                description,
                is_default: myDashboards.length === 0,
            });
            addDashboard(newDashboard);
            setCurrentDashboard(newDashboard);
            setIsCreateModalOpen(false);
        } catch (err) {
            console.error('Failed to create dashboard:', err);
            setError('Failed to create dashboard');
        }
    };

    const handleDeleteDashboard = async (id: number) => {
        try {
            await dashboardService.deleteDashboard(id);
            removeDashboard(id);
            if (currentDashboard?.id === id) {
                const remaining = myDashboards.filter((d) => d.id !== id);
                setCurrentDashboard(remaining[0] || null);
            }
        } catch (err) {
            console.error('Failed to delete dashboard:', err);
            setError('Failed to delete dashboard');
        }
    };

    const handleSetDefault = async (id: number) => {
        if (!currentPage) return;

        try {
            await dashboardService.setDefaultDashboard(id, currentPage.id);
            // Update all dashboards in store
            myDashboards.forEach((d) => {
                updateDashboard(d.id, { is_default: d.id === id });
            });
        } catch (err) {
            console.error('Failed to set default:', err);
            setError('Failed to set default dashboard');
        }
    };

    const handleEditDashboard = async (name: string, description: string) => {
        if (!currentDashboard) return;

        try {
            const updated = await dashboardService.updateDashboard(currentDashboard.id, {
                name,
                description,
            });
            updateDashboard(currentDashboard.id, { name: updated.name, description: updated.description });
            setIsEditModalOpen(false);
        } catch (err) {
            console.error('Failed to update dashboard:', err);
            setError('Failed to update dashboard');
        }
    };

    // Widget actions
    const handleAddWidgetFromTemplate = async (template: WidgetTemplate) => {
        if (!currentDashboard) return;

        try {
            const offsetMultiplier = widgets.length % 5;
            const positionX = 2 + offsetMultiplier;
            const positionY = 1 + offsetMultiplier;

            let newWidget;
            if (template.source_type === 'report_builder') {
                // Published report builder template — use the published template endpoint
                newWidget = await widgetService.createWidgetFromPublishedTemplate(currentDashboard.id, {
                    published_template_id: String(template.id),
                    position_x: positionX,
                    position_y: positionY,
                    sort_index: widgets.length,
                });
            } else {
                // Seeded template — use the standard template endpoint
                newWidget = await widgetService.createWidgetFromTemplate(currentDashboard.id, {
                    template_id: template.id,
                    position_x: positionX,
                    position_y: positionY,
                    sort_index: widgets.length,
                });
            }
            addWidget(newWidget);
            setIsTemplateLibraryOpen(false);
        } catch (err) {
            console.error('Failed to add widget from template:', err);
            setError('Failed to add widget');
        }
    };

    const handleBatchAddWidgetsFromTemplate = async (selectedTemplates: WidgetTemplate[]) => {
        if (!currentDashboard || selectedTemplates.length === 0) return;

        try {
            const baseOffset = widgets.length;
            const batchItems = selectedTemplates.map((template, i) => ({
                template_id: String(template.id),
                position_x: 2 + ((baseOffset + i) % 5),
                position_y: 1 + ((baseOffset + i) % 5),
                sort_index: baseOffset + i,
            }));

            // Separate published (report_builder) templates from seeded templates
            const publishedTemplates = selectedTemplates.filter(t => t.source_type === 'report_builder');
            const seededTemplates = selectedTemplates.filter(t => t.source_type !== 'report_builder');

            // Batch add seeded templates
            if (seededTemplates.length > 0) {
                const seededItems = seededTemplates.map((template, i) => ({
                    template_id: String(template.id),
                    position_x: 2 + ((baseOffset + i) % 5),
                    position_y: 1 + ((baseOffset + i) % 5),
                    sort_index: baseOffset + i,
                }));
                const newWidgets = await widgetService.batchCreateWidgetsFromTemplate(
                    currentDashboard.id,
                    seededItems
                );
                newWidgets.forEach(w => addWidget(w));
            }

            // Published templates don't have batch endpoint — add individually
            for (let i = 0; i < publishedTemplates.length; i++) {
                const template = publishedTemplates[i];
                const idx = baseOffset + seededTemplates.length + i;
                const newWidget = await widgetService.createWidgetFromPublishedTemplate(currentDashboard.id, {
                    published_template_id: String(template.id),
                    position_x: 2 + (idx % 5),
                    position_y: 1 + (idx % 5),
                    sort_index: idx,
                });
                addWidget(newWidget);
            }

            setIsTemplateLibraryOpen(false);
        } catch (err) {
            console.error('Failed to batch add widgets:', err);
            setError('Failed to add widgets');
        }
    };

    const handleCloneWidget = useCallback(async (id: string | number) => {
        const widgetId = typeof id === 'string' ? parseInt(id) : id;
        if (!currentDashboard) return;
        try {
            const cloned = await widgetService.cloneWidget(widgetId, {
                target_dashboard_id: currentDashboard.id,
                position_x: 0,
                position_y: 0,
                sort_index: widgets.length,
            });
            addWidget(cloned);
        } catch (err) {
            console.error('Failed to clone widget:', err);
        }
    }, [currentDashboard, widgets.length, addWidget]);

    const handleRemoveWidget = async (id: string | number) => {
        const widgetId = typeof id === 'string' ? parseInt(id) : id;
        try {
            await widgetService.deleteWidget(widgetId);
            removeWidget(widgetId);
        } catch (err) {
            console.error('Failed to delete widget:', err);
        }
    };

    const handleLayoutChange = useCallback(
        async (layout: any[]) => {
            if (activeTab === 'shared-dashboards') return;

            const updates: WidgetLayoutUpdate[] = [];

            layout.forEach((item) => {
                const widgetId = parseInt(item.i);
                const widget = widgets.find((w) => String(w.id) === item.i);

                if (
                    widget &&
                    (widget.position_x !== item.x ||
                        widget.position_y !== item.y ||
                        widget.width !== item.w ||
                        widget.height !== item.h)
                ) {
                    updates.push({
                        id: widgetId,
                        position_x: item.x,
                        position_y: item.y,
                        width: item.w,
                        height: item.h,
                        sort_index: widget.sort_index,
                    });
                }
            });

            if (updates.length === 0) return;

            // Update local state via store
            updateWidgetLayouts(updates);

            // Persist to backend
            try {
                await Promise.all(
                    updates.map((update) =>
                        widgetService.updateWidgetLayout(update.id, {
                            position_x: update.position_x,
                            position_y: update.position_y,
                            width: update.width,
                            height: update.height,
                        })
                    )
                );
            } catch (err) {
                console.error('Failed to update layout:', err);
            }
        },
        [activeTab, widgets, updateWidgetLayouts]
    );

    const handleChartTypeChange = useCallback(async (widgetId: number, chartType: ChartType) => {
        const widget = widgets.find((w) => String(w.id) === String(widgetId));
        if (!widget) return;

        try {
            const currentVisualizationConfig = parseConfig(widget.visualization_config);
            const { chartType: _, chart_type: __, ...restConfig } = currentVisualizationConfig as any;
            const newVisualizationConfig = { ...restConfig, chart_type: chartType };

            const updatedWidget = await widgetService.updateWidgetVisualizationConfig(widgetId, newVisualizationConfig);
            updateWidget(widgetId, { visualization_config: updatedWidget.visualization_config });
        } catch (err) {
            console.error('Failed to update chart type:', err);
        }
    }, [widgets, updateWidget]);

    const handleWidgetFiltersChange = useCallback(async (widgetId: number, newFilters: QueryFilter[]) => {
        try {
            await widgetService.updateWidgetFilters(widgetId, newFilters);
            updateWidget(widgetId, { filters: newFilters });
        } catch (err) {
            console.error('Failed to update widget filters:', err);
        }
    }, [updateWidget]);

    const handleTitleChange = useCallback(async (widgetId: number, newTitle: string) => {
        try {
            const updatedWidget = await widgetService.updateWidgetTitle(widgetId, newTitle);
            updateWidget(widgetId, { title: updatedWidget.title });
        } catch (err) {
            console.error('Failed to update title:', err);
        }
    }, [updateWidget]);

    // Send to Dashboard handlers
    const handleSendToDashboard = useCallback(async (widgetId: string | number, targetDashboardId: number) => {
        const id = typeof widgetId === 'string' ? parseInt(widgetId) : widgetId;
        try {
            await widgetService.moveWidget(id, {
                target_dashboard_id: targetDashboardId,
            });
            // Remove widget from current dashboard
            removeWidget(id);
        } catch (err) {
            console.error('Failed to send widget to dashboard:', err);
            setError('Failed to send widget to dashboard');
        }
    }, [removeWidget, setError]);

    const handleFetchShareDestinations = useCallback(async (widgetId: string | number) => {
        const id = typeof widgetId === 'string' ? parseInt(widgetId) : widgetId;
        return widgetService.getShareDestinations(id);
    }, []);

    // Generate field label from filter_key (snake_case to Title Case)
    const getFieldLabel = useCallback((filterKey: string): string => {
        return filterKey
            .split('_')
            .filter(word => word !== 'id') // Remove 'id' suffix (e.g., location_id -> Location)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }, []);

    // Build fieldLabels dynamically from available filters
    const buildFieldLabels = useCallback((availableFilters: any[]): Record<string, string> => {
        const labels: Record<string, string> = {};
        availableFilters.forEach(filter => {
            if (filter.filter_key) {
                labels[filter.filter_key] = getFieldLabel(filter.filter_key);
            }
        });
        return labels;
    }, [getFieldLabel]);

    // Get available filters from widget's queryConfig or capabilities
    const getAvailableFiltersForWidget = useCallback((widget: DashboardWidget): any[] => {
        // Parse query_config if it's a string (API may return JSON string)
        let queryConfig: Record<string, unknown> | null = null;
        if (typeof widget.query_config === 'string') {
            try {
                queryConfig = JSON.parse(widget.query_config);
            } catch {
                queryConfig = null;
            }
        } else {
            queryConfig = widget.query_config as Record<string, unknown>;
        }

        // Parse capabilities if it's a string
        let capabilities: any = null;
        if (typeof widget.capabilities === 'string') {
            try {
                capabilities = JSON.parse(widget.capabilities);
            } catch {
                capabilities = null;
            }
        } else {
            capabilities = widget.capabilities;
        }

        // First try query_config.filters
        if (queryConfig?.filters && Array.isArray(queryConfig.filters) && queryConfig.filters.length > 0) {
            const firstFilter = (queryConfig.filters as any[])[0];
            if (firstFilter?.filter_key) {
                return queryConfig.filters as any[];
            }
        }

        // Fallback to capabilities.query.filters
        if (capabilities?.query?.filters && Array.isArray(capabilities.query.filters) && capabilities.query.filters.length > 0) {
            const firstFilter = capabilities.query.filters[0];
            if (firstFilter?.filter_key) {
                return capabilities.query.filters;
            }
        }

        return [];
    }, []);

    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return 'Unknown';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const isReadOnly = activeTab === 'shared-dashboards';
    // Use the appropriate widgets based on active tab
    const activeWidgets = activeTab === 'shared-dashboards' ? sharedWidgets : widgets;
    const activeWidgetsLoading = activeTab === 'shared-dashboards' ? sharedWidgetsLoading : widgetsLoading;
    const canvasWidgets = activeWidgets.map(convertToCanvasWidget);

    // Render widget content
    const renderWidgetContent = (widget: { id: string; title: string; data: unknown; config: unknown }) => {
        const dashboardWidget = activeWidgets.find((w) => String(w.id) === widget.id);
        if (!dashboardWidget) return null;

        const queryConfig = dashboardWidget.query_config as any;
        const visualizationConfig = parseConfig(dashboardWidget.visualization_config);
        const metricKey = dashboardWidget.metric_key || queryConfig?.metric_key;
        const currentChartType = (visualizationConfig?.chart_type as string) || undefined;
        const availableFilters = getAvailableFiltersForWidget(dashboardWidget);
        const fieldLabels = buildFieldLabels(availableFilters);

        // Read fixed scoping value from widget
        const scoping = (dashboardWidget.scoping || queryConfig?.scoping) as string | undefined;

        return (
            <WidgetWrapper
                widgetId={dashboardWidget.id}
                title={dashboardWidget.title}
                templateKey={dashboardWidget.template_key}
                metricKey={metricKey}
                queryConfig={dashboardWidget.query_config}
                visualizationConfig={dashboardWidget.visualization_config}
                chartType={currentChartType}
                chartData={queryConfig?.data}
                filters={(dashboardWidget.filters as QueryFilter[]) || []}
                availableFilters={availableFilters}
                fieldLabels={fieldLabels}
                capabilities={dashboardWidget.capabilities as any}
                currentChartType={currentChartType}
                onChartTypeChange={isReadOnly ? undefined : (chartType) => handleChartTypeChange(dashboardWidget.id, chartType)}
                onFiltersChange={isReadOnly ? undefined : async (filters) => handleWidgetFiltersChange(dashboardWidget.id, filters)}
                onClone={isReadOnly ? undefined : handleCloneWidget}
                onRemove={isReadOnly ? undefined : handleRemoveWidget}
                onTitleChange={isReadOnly ? undefined : async (title) => handleTitleChange(dashboardWidget.id, title)}
                onSendToDashboard={isReadOnly ? undefined : handleSendToDashboard}
                onFetchShareDestinations={isReadOnly ? undefined : handleFetchShareDestinations}
                ownerUserId={isViewingSharedDashboard ? sharedDashboardOwner?.user_id : undefined}
                readOnly={isReadOnly}
                scoping={scoping}
                onDrillToDetail={handleDrillToDetail}
            />
        );
    };

    // Tab navigation component
    const TabNavigation = () => (
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
                onClick={() => handleTabChange('my-dashboards')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'my-dashboards'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
            >
                <span className="flex items-center gap-2">
                    <User size={16} />
                    My Dashboards
                </span>
            </button>
            <button
                onClick={() => handleTabChange('shared-dashboards')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'shared-dashboards'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
            >
                <span className="flex items-center gap-2">
                    <Users size={16} />
                    Shared Dashboards
                </span>
            </button>
        </div>
    );

    // ============================================
    // My Dashboards Tab - Widget Canvas View
    // ============================================
    if (activeTab === 'my-dashboards') {
        return (
            <div className="flex flex-col h-full bg-white">
                {/* Header with tabs */}
                <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
                    <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-4">
                                <DashboardDropdown
                                    dashboards={myDashboards}
                                    currentDashboard={currentDashboard}
                                    onSelect={setCurrentDashboard}
                                    onCreateNew={() => setIsCreateModalOpen(true)}
                                    onSetDefault={handleSetDefault}
                                    onDelete={handleDeleteDashboard}
                                />
                                {currentDashboard && (
                                    <>
                                        <button
                                            onClick={() => setIsEditModalOpen(true)}
                                            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                                            title="Edit dashboard"
                                        >
                                            <PencilSimple size={16} className="text-gray-400" />
                                        </button>
                                        <span className="text-sm text-gray-500">
                                            {widgets.length} widget{widgets.length !== 1 ? 's' : ''}
                                        </span>
                                    </>
                                )}
                            </div>
                            {currentDashboard?.description && (
                                <p className="text-sm text-gray-400 pl-[4px]">
                                    {currentDashboard.description}
                                </p>
                            )}
                        </div>
                        <div className="flex items-center gap-4">
                            {currentDashboard && (
                                <>
                                    <Button
                                        onClick={() => setIsTemplateLibraryOpen(true)}
                                    >
                                        <Books size={18} weight="bold" />
                                        Add Widget
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => setIsShareModalOpen(true)}
                                    >
                                        <ShareNetwork size={18} weight="bold" />
                                        Share
                                    </Button>
                                </>
                            )}
                            <TabNavigation />
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 flex flex-col min-h-0 p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="text-gray-500">Loading dashboards...</div>
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="text-red-500">{error}</div>
                        </div>
                    ) : myDashboards.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-center">
                            <Layout size={48} className="text-gray-300 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No dashboards yet</h3>
                            <p className="text-gray-500 max-w-md mb-6">
                                Create your first dashboard to start adding widgets.
                            </p>
                            <Button
                                onClick={() => setIsCreateModalOpen(true)}
                                size="lg"
                            >
                                <Plus size={18} weight="bold" />
                                Create Dashboard
                            </Button>
                        </div>
                    ) : currentDashboard && widgetsLoading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="text-gray-500">Loading widgets...</div>
                        </div>
                    ) : currentDashboard && widgets.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-center">
                            <Layout size={48} className="text-gray-300 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No widgets</h3>
                            <p className="text-gray-500 mb-6">Add widgets to your dashboard.</p>
                            <Button
                                onClick={() => setIsTemplateLibraryOpen(true)}
                                size="lg"
                            >
                                <Books size={18} weight="bold" />
                                Add Widget
                            </Button>
                        </div>
                    ) : currentDashboard ? (
                        <div className="bg-white rounded-xl flex-1 min-h-0 overflow-auto">
                            <WidgetCanvas
                                widgets={canvasWidgets}
                                onRemoveWidget={handleRemoveWidget}
                                onLayoutChange={handleLayoutChange}
                                renderWidget={renderWidgetContent}
                            />
                        </div>
                    ) : null}
                </div>

                {/* Modals */}
                <CreateDashboardModal
                    isOpen={isCreateModalOpen}
                    onClose={() => setIsCreateModalOpen(false)}
                    onSubmit={handleCreateDashboard}
                />
                <WidgetTemplateLibrary
                    isOpen={isTemplateLibraryOpen}
                    onClose={() => setIsTemplateLibraryOpen(false)}
                    templates={templates}
                    onAddTemplate={handleAddWidgetFromTemplate}
                    onAddTemplates={handleBatchAddWidgetsFromTemplate}
                    loading={templatesLoading}
                />
                {currentDashboard && (
                    <ShareDashboardModal
                        isOpen={isShareModalOpen}
                        onClose={() => setIsShareModalOpen(false)}
                        dashboard={currentDashboard}
                    />
                )}
                {currentDashboard && (
                    <EditDashboardModal
                        isOpen={isEditModalOpen}
                        onClose={() => setIsEditModalOpen(false)}
                        onSubmit={handleEditDashboard}
                        dashboard={currentDashboard}
                    />
                )}

                {/* Drill-to-Detail Modal */}
                {drillToDetailContext && (
                    <DrillToDetailModal
                        isOpen={true}
                        onClose={() => setDrillToDetailContext(null)}
                        context={drillToDetailContext}
                    />
                )}

            </div>
        );
    }

    // ============================================
    // Shared Dashboards Tab - Library List View
    // ============================================

    // Shared Dashboard Detail View
    if (selectedSharedDashboard) {
        const handleBackToList = () => {
            clearSharedSelection();
        };

        return (
            <div className="flex flex-col h-full bg-white">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleBackToList}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Back to dashboard list"
                        >
                            <ArrowLeft size={20} className="text-gray-600" />
                        </button>
                        <div className="flex-1">
                            <h1 className="text-xl font-semibold text-gray-900">{selectedSharedDashboard.name}</h1>
                            {selectedSharedDashboard.description && (
                                <p className="text-sm text-gray-500">{selectedSharedDashboard.description}</p>
                            )}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSelectSharedDashboard(selectedSharedDashboard)}
                            disabled={sharedWidgetsLoading}
                            title="Refresh dashboard"
                        >
                            <ArrowsClockwise
                                size={16}
                                className={sharedWidgetsLoading ? 'animate-spin' : ''}
                            />
                            Refresh
                        </Button>
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium">
                            View Only
                        </span>
                        <TabNavigation />
                    </div>
                </div>

                {/* Owner Info Banner */}
                {sharedDashboardOwner && (
                    <div className="bg-blue-50 border-b border-blue-100 px-6 py-2 flex items-center gap-2 text-sm text-blue-700">
                        <User size={16} />
                        <span>
                            Viewing <strong>{sharedDashboardOwner.first_name} {sharedDashboardOwner.last_name}</strong>'s dashboard data
                        </span>
                    </div>
                )}

                {/* Widget Canvas */}
                <div className="flex-1 flex flex-col min-h-0 p-6">
                    {activeWidgetsLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-gray-500">Loading widgets...</div>
                        </div>
                    ) : activeWidgets.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-center">
                            <Layout size={48} className="text-gray-300 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No widgets</h3>
                            <p className="text-gray-500">This dashboard doesn't have any widgets yet.</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl flex-1 min-h-0 overflow-auto">
                            <WidgetCanvas
                                widgets={canvasWidgets}
                                onLayoutChange={() => { }}
                                onRemoveWidget={() => { }}
                                isDraggable={false}
                                isResizable={false}
                                renderWidget={renderWidgetContent}
                            />
                        </div>
                    )}
                </div>

                {/* Drill-to-Detail Modal */}
                {drillToDetailContext && (
                    <DrillToDetailModal
                        isOpen={true}
                        onClose={() => setDrillToDetailContext(null)}
                        context={drillToDetailContext}
                    />
                )}

            </div>
        );
    }

    // Shared Dashboard List View
    return (
        <div className="flex flex-col h-full bg-white">
            {/* Header with tabs */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">Dashboards shared with you by other users</p>
                    <TabNavigation />
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-gray-500">Loading dashboards...</div>
                    </div>
                ) : sharedError ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-red-500">{sharedError}</div>
                    </div>
                ) : sharedDashboards.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-center">
                        <Users size={48} className="text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No shared dashboards</h3>
                        <p className="text-gray-500 max-w-md">
                            When other users share dashboards with you, they will appear here.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {sharedDashboards.map((dashboard) => {
                            const sharedDashboard = dashboard as SharedDashboard;
                            const ownerName = sharedDashboard.owner_first_name
                                ? `${sharedDashboard.owner_first_name} ${sharedDashboard.owner_last_name}`
                                : (dashboard as any).shared_by_name || 'Unknown';

                            return (
                                <button
                                    key={dashboard.id}
                                    onClick={() => handleSelectSharedDashboard(dashboard)}
                                    className="bg-white rounded-xl border border-gray-200 p-5 text-left hover:border-blue-300 hover:shadow-md transition-all group"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
                                            <Layout size={24} className="text-blue-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                                                {dashboard.name}
                                            </h3>
                                            {dashboard.description && (
                                                <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                                    {dashboard.description}
                                                </p>
                                            )}
                                            <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                                                <span className="flex items-center gap-1">
                                                    <Users size={12} />
                                                    {ownerName}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Calendar size={12} />
                                                    {formatDate((dashboard as any).shared_at || dashboard.created_at)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DashboardsPage;
